/**
 * 全景场景地图挂点（WGS84 → 当前底图显示坐标）
 * 默认图标小于队员坐标点；可由后台指定图标库图片与像素大小。
 */
import mapboxgl from 'mapbox-gl';
import { toMapLngLat } from './basemapCoord';

const DEFAULT_SIZE = 22;

function createDefaultPanoramaSvg(size) {
  const s = Math.max(12, Math.min(48, Number(size) || DEFAULT_SIZE));
  return `
    <svg width="${s}" height="${s}" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
      <circle cx="17" cy="17" r="15" fill="#1d4ed8" stroke="#fff" stroke-width="2.5"/>
      <path d="M8 17a9 9 0 0 1 18 0" fill="none" stroke="#93c5fd" stroke-width="1.8"/>
      <path d="M11 17a6 6 0 0 1 12 0" fill="none" stroke="#bfdbfe" stroke-width="1.5"/>
      <circle cx="17" cy="17" r="2.6" fill="#fff"/>
    </svg>
  `;
}

/**
 * @param {string} title
 * @param {{ size?: number, iconUrl?: string|null }} style
 */
export function createPanoramaPinElement(title, style = {}) {
  const size = Math.max(12, Math.min(48, Number(style.size) || DEFAULT_SIZE));
  const iconUrl = style.iconUrl ? String(style.iconUrl) : '';
  const el = document.createElement('button');
  el.type = 'button';
  el.title = title || '全景';
  el.setAttribute('aria-label', title || '全景挂点');
  el.style.cssText = [
    `width:${size}px`,
    `height:${size}px`,
    'padding:0',
    'margin:0',
    'border:none',
    'background:transparent',
    'cursor:pointer',
    'line-height:0',
    'filter:drop-shadow(0 1px 3px rgba(15,23,42,0.35))'
  ].join(';');

  if (iconUrl) {
    const img = document.createElement('img');
    img.src = iconUrl;
    img.alt = title || '全景';
    img.draggable = false;
    img.style.cssText = `width:${size}px;height:${size}px;object-fit:contain;display:block;pointer-events:none;`;
    el.appendChild(img);
  } else {
    el.innerHTML = createDefaultPanoramaSvg(size);
  }
  return el;
}

function styleKeyOf(style = {}) {
  return `${style.iconUrl || ''}|${Number(style.size) || DEFAULT_SIZE}`;
}

/**
 * @param {Map} markersMap sceneId -> marker
 * @param {{ onOpen?: (pin) => void, size?: number, iconUrl?: string|null }} options
 */
export function syncPanoramaMapPins(map, pins = [], markersMap, basemapId, options = {}) {
  if (!map || !markersMap) return;
  const style = {
    size: options.size,
    iconUrl: options.iconUrl || null
  };
  const sk = styleKeyOf(style);
  const want = new Set();

  (pins || []).forEach((pin) => {
    const id = String(pin.sceneId);
    const lon = Number(pin.lon);
    const lat = Number(pin.lat);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    want.add(id);
    const display = toMapLngLat(lon, lat, basemapId, map) || [lon, lat];
    const title = `${pin.projectTitle || '全景'} · ${pin.sceneName || `#${id}`}`;

    const existing = markersMap.get(id);
    if (existing && existing.__zhxtStyleKey === sk) {
      try {
        existing.setLngLat(display);
        existing.getElement().title = title;
        existing.__zhxtPin = pin;
      } catch (_) { /* ignore */ }
      return;
    }
    if (existing) {
      try { existing.remove(); } catch (_) { /* ignore */ }
      markersMap.delete(id);
    }

    const el = createPanoramaPinElement(title, style);
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      options.onOpen?.(pin);
    });
    const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(display)
      .addTo(map);
    marker.__zhxtPin = pin;
    marker.__zhxtStyleKey = sk;
    markersMap.set(id, marker);
  });

  [...markersMap.keys()].forEach((id) => {
    if (want.has(id)) return;
    try { markersMap.get(id)?.remove?.(); } catch (_) { /* ignore */ }
    markersMap.delete(id);
  });
}

export function clearPanoramaMapPins(markersMap) {
  if (!markersMap) return;
  [...markersMap.keys()].forEach((id) => {
    try { markersMap.get(id)?.remove?.(); } catch (_) { /* ignore */ }
    markersMap.delete(id);
  });
}
